/**
 * @fileoverview Module for `src/lib/jwt.js`.
 */

/**
 * JWT decoding helpers for browser-side tokens.
 */

/**
 * Decodes a JWT payload without verifying signature.
 *
 * JWT segments are base64url, which `atob` rejects whenever a payload contains `-` or `_`, so those
 * are mapped back to base64 first; `atob` accepts the missing padding. The payload is UTF-8 JSON,
 * while `atob` returns one character per byte, so the bytes are decoded as UTF-8.
 *
 * @param {string} token - JWT string.
 * @returns {Object} Decoded payload.
 * @throws {Error} When the token has no payload segment or the payload is not base64url JSON.
 */
function decodeToken(token) {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const binary = window.atob(base64);
  const percentEncoded = Array.from(binary, (char) => `%${ char.charCodeAt(0).toString(16).padStart(2, '0') }`);
  return JSON.parse(decodeURIComponent(percentEncoded.join('')));
}

/**
 * Decodes a JWT payload without verifying signature, or returns undefined for a missing or
 * unreadable token.
 *
 * @param {string} [token] - JWT string.
 * @returns {Object|undefined} Decoded payload.
 */
function tryDecodeToken(token) {
  if (typeof token !== 'string' || !token) {
    return undefined;
  }
  try {
    return decodeToken(token);
  } catch {
    return undefined;
  }
}

/**
 * Reads a claim from the supplemental token embedded in `access_token` storage.
 *
 * @param {string} claim - Claim key.
 * @returns {*} Claim value.
 */
function extraTokenClaim(claim) {
  const accessToken = localStorage.getItem('access_token') || '';
  const extraToken = accessToken.split(':').length > 1 ? accessToken.split(':')[1] : undefined;
  const decodedExtraToken = extraToken ? decodeToken(extraToken) : {};
  return decodedExtraToken[claim];
}

export {
  decodeToken,
  extraTokenClaim,
  tryDecodeToken,
};
